/**
 * Loop bundle discount guard — money-safety net for reduced bundle lines.
 *
 * A Loop preset-bundle discount is written ONCE at add time, sized for the pack that was
 * added (e.g. discountComputedValue = $223.50 for the 6-bottle Wild Olive Oil bundle = 25%
 * of 6×$149). If that bundle line's quantity is later REDUCED without going back through the
 * PDP widget — e.g. via the Rebuy Smart Cart qty stepper, which bypasses the theme drawer's
 * no-stepper guard on bundle lines — the discount is NOT recomputed. Shopify then caps the
 * oversized discount at the (now smaller) line value and the product ships FREE.
 *
 * Reproduced + root-caused from order #24345 (6-bottle bundle reduced to 1 in the Rebuy cart
 * → −$149 on a $149 line → $0, customer paid shipping only).
 *
 * This guard re-caps discountComputedValue on every cart mutation so it can never exceed the
 * legitimate percentage of the line's ACTUAL current subtotal. It only ever REDUCES an
 * oversized discount (min(stored, pct × subtotal)) — it never grows one, so it can't create a
 * new leak. A bundle whose lines are gone entirely has its stale discount dropped.
 */
(function () {
  const KEY = '_loopBundleDiscountAttributes';
  let running = false;
  let lastWritten = '';

  async function heal() {
    if (running) return;
    running = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const raw = cart && cart.attributes && cart.attributes[KEY];
      if (!raw) return;

      let attrs;
      try { attrs = JSON.parse(raw); } catch { return; }
      if (!attrs || typeof attrs !== 'object') return;

      // Pre-discount subtotal (cents) grouped by bundle transaction id.
      const subByTxn = {};
      for (const item of cart.items || []) {
        const p = item.properties || {};
        const txn = p._loopBundleTxnId || p._bundleId;
        if (!txn) continue;
        subByTxn[txn] = (subByTxn[txn] || 0) + (item.original_line_price || 0);
      }

      let changed = false;
      for (const txn of Object.keys(attrs)) {
        const a = attrs[txn];
        if (!a) continue;

        const subCents = subByTxn[txn];
        // No line left for this txn → drop the stale discount so it can't apply to anything.
        if (!subCents) { delete attrs[txn]; changed = true; continue; }

        const sub = subCents / 100;
        // Highest legitimate value this discount may have = its own percentage of the
        // current line subtotal. For subscription bundles discountComputedValue is already
        // <= that, so this is a no-op for healthy carts and only bites when a line shrank.
        let cap;
        if (String(a.discountType).toUpperCase() === 'PERCENTAGE') {
          cap = (Number(a.discountValue) || 0) / 100 * sub;
        } else {
          cap = sub; // non-percentage: at minimum, never exceed the line value
        }
        cap = Math.round(cap * 100) / 100;

        const current = Number(a.discountComputedValue) || 0;
        if (current > cap + 0.001) {
          a.discountComputedValue = cap;
          changed = true;
        }
      }

      if (!changed) return;

      const next = JSON.stringify(attrs);
      if (next === lastWritten) return; // never write the same correction twice
      lastWritten = next;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [KEY]: next } }),
      });
    } catch (e) {
      /* never block the storefront on a guard failure */
    } finally {
      running = false;
    }
  }

  let t;
  function schedule() { clearTimeout(t); t = setTimeout(heal, 350); }

  // React to every signal a cart may have changed — theme, Loop, or Rebuy.
  ['cart:update', 'cart:change', 'cart:refresh', 'cart:added',
   'rebuy:cart.change', 'rebuy:cart.ready', 'rebuy:smartcart.ready'].forEach((evt) =>
    document.addEventListener(evt, schedule),
  );
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });

  // Belt-and-suspenders: catch ANY cart mutation (incl. Rebuy Smart Cart's qty stepper) even
  // if it fires no known event, by observing cart write requests. Our own update.js write is
  // idempotent (lastWritten guard), so this can't loop.
  const origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      let url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { /* */ }
      const p = origFetch.apply(this, arguments);
      if (/\/cart\/(change|add|update|clear)(\.js|\.json)?(\?|$)/.test(url)) {
        try { p.then(() => schedule()).catch(() => {}); } catch (e) { /* */ }
      }
      return p;
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
